import React, { Fragment, memo, useEffect, useRef, useState } from 'react';
import { TaskRole } from "@/components/scheduler/schedulerTypes";
import { roleDefinitions } from './RoleFilter';
import { User } from 'lucide-react';
import { getPersonShortName } from '@/utils/nameFormatters';
import { 
  attachClosestEdge, 
  extractClosestEdge,
  type Edge 
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { createPortal } from 'react-dom';
import { useBoardContext } from './BoardContext';
import { useColumnContext } from './ColumnContext';
import { token } from '@atlaskit/tokens';

interface Task {
  id: string;
  content: string;
  description?: string;
  role: TaskRole;
  sprint_id?: string | null;
  employee_id?: number | null;
  employee_name?: string | null;
}

interface TaskCardProps {
  task: Task;
  index: number;
  onEdit?: (task: Task) => void;
}

// Card state types
type State =
  | { type: 'idle' }
  | { type: 'preview'; container: HTMLElement; rect: DOMRect }
  | { type: 'dragging' };

// Stable state objects
const idleState: State = { type: 'idle' };
const draggingState: State = { type: 'dragging' };

export const TaskCard = memo(function TaskCard({ task, index, onEdit }: TaskCardProps) {
  const role = task.role;
  const roleInfo = roleDefinitions[role] || { color: 'bg-gray-500', displayName: 'Unknown' };
  const cardRef = useRef<HTMLDivElement>(null);
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
  const [state, setState] = useState<State>(idleState);
  
  // Board context for drag and drop operations
  const { instanceId, registerCard, reorderTask, moveTask } = useBoardContext();
  const { columnId, getTaskIndex } = useColumnContext();

  // Register this card with the registry
  useEffect(() => {
    if (!cardRef.current) return;
    
    return registerCard({
      taskId: task.id,
      entry: {
        element: cardRef.current
      }
    });
  }, [registerCard, task.id]);

  // Set up drag and drop
  useEffect(() => {
    const element = cardRef.current;
    if (!element) return;
    
    return combine(
      // Make this card draggable
      draggable({
        element,
        getInitialData: () => ({ 
          type: 'card', 
          itemId: task.id, 
          instanceId,
          sourceColumnId: columnId,
          sourceIndex: index
        }),
        onGenerateDragPreview: ({ location, source, nativeSetDragImage }) => {
          const rect = source.element.getBoundingClientRect();

          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({
              element,
              input: location.current.input,
            }),
            render({ container }) {
              setState({ type: 'preview', container, rect });
              return () => setState(draggingState);
            },
          });
        },
        onDragStart: () => setState(draggingState),
        onDrop: () => setState(idleState),
      }),
      
      // Make this card a drop target for other cards
      dropTargetForElements({
        element,
        canDrop: ({ source }) => {
          return source.data.instanceId === instanceId && 
                 source.data.type === 'card' &&
                 source.data.itemId !== task.id; // Can't drop onto self
        },
        getIsSticky: () => true,
        getData: ({ input, element }) => {
          const data = { 
            type: 'card', 
            itemId: task.id,
            targetColumnId: columnId,
            targetIndex: index
          };
          
          return attachClosestEdge(data, {
            input,
            element,
            allowedEdges: ['top', 'bottom'],
          });
        },
        onDragEnter: (args) => {
          setClosestEdge(extractClosestEdge(args.self.data));
        },
        onDrag: (args) => {
          setClosestEdge(extractClosestEdge(args.self.data));
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      })
    );
  }, [instanceId, task.id, columnId, index]);

  const handleTaskClick = (e: React.MouseEvent) => {
    if (onEdit) {
      onEdit(task);
    }
  };
  
  return (
    <div
      data-task-id={task.id}
      data-column-id={columnId}
      data-task-index={index}
    >
      <div
        ref={cardRef}
        className={`relative p-3 mb-2 bg-card rounded shadow-sm border border-border 
          hover:border-border/50 transition-colors
          ${state.type === 'dragging' ? 'opacity-40' : ''}
          ${state.type === 'idle' ? 'cursor-grab' : ''}`
        }
        data-testid={`task-${task.id}`}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center">
            <div className={`h-2 w-2 rounded-full ${roleInfo.color} mr-2`} />
            <span className="text-xs text-muted-foreground">
              {roleInfo.displayName}
            </span>
          </div>
          {task.employee_id && (
            <div className="flex items-center text-xs text-muted-foreground">
              <User className="h-3 w-3 mr-1" />
              <span>{getPersonShortName(task.employee_name)}</span>
            </div>
          )}
        </div>
        <div 
          className="font-medium cursor-pointer hover:text-primary hover:underline"
          onClick={handleTaskClick}
        >
          {task.content}
        </div>
        {task.description && (
          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {task.description}
          </div>
        )}
        
        {/* Drop indicator */}
        {closestEdge && (
          <DropIndicator edge={closestEdge} gap={token('space.100', '8px')} />
        )}
      </div>
      
      {/* Drag preview portal */}
      {state.type === 'preview' && createPortal(
        <div
          style={{
            boxSizing: 'border-box',
            width: state.rect.width,
            height: state.rect.height,
          }}
        >
          <div className="p-3 bg-card rounded shadow-sm border border-border">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <div className={`h-2 w-2 rounded-full ${roleInfo.color} mr-2`} />
                <span className="text-xs text-muted-foreground">
                  {roleInfo.displayName}
                </span>
              </div>
            </div>
            <div className="font-medium">
              {task.content}
            </div>
            {task.description && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {task.description}
              </div>
            )}
          </div>
        </div>,
        state.container
      )}
    </div>
  );
});
